/**
 * Utility functions for handling filing types and their display properties
 */

export interface FilingTypeInfo {
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

export const getFilingTypeTag = (filingType: string): FilingTypeInfo => {
  const typeMap: { [key: string]: FilingTypeInfo } = {
    'COMMENT': { label: 'Comment', bgColor: 'bg-blue-100', textColor: 'text-blue-700', borderColor: 'border-blue-200' },
    'ERRATA': { label: 'Errata', bgColor: 'bg-amber-100', textColor: 'text-amber-700', borderColor: 'border-amber-200' },
    'LETTER': { label: 'Letter', bgColor: 'bg-slate-100', textColor: 'text-slate-700', borderColor: 'border-slate-200' },
    'NOTICE': { label: 'Notice', bgColor: 'bg-violet-100', textColor: 'text-violet-700', borderColor: 'border-violet-200' },
    'NPRM': { label: 'NPRM', bgColor: 'bg-green-100', textColor: 'text-green-700', borderColor: 'border-green-200' },
    'PUB NOTICE': { label: 'Pub Notice', bgColor: 'bg-teal-100', textColor: 'text-teal-700', borderColor: 'border-teal-200' },
    'PUBLIC DRAFT': { label: 'Public Draft', bgColor: 'bg-rose-100', textColor: 'text-rose-700', borderColor: 'border-rose-200' },
    'REPLY': { label: 'Reply', bgColor: 'bg-orange-100', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
    'REPLY_COMMENT': { label: 'Reply Comment', bgColor: 'bg-orange-100', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
    'REPLY COMM': { label: 'Reply Comment', bgColor: 'bg-orange-100', textColor: 'text-orange-700', borderColor: 'border-orange-200' },
    'EX_PARTE': { label: 'Ex Parte', bgColor: 'bg-red-100', textColor: 'text-red-700', borderColor: 'border-red-200' },
    'EX PARTE': { label: 'Ex Parte', bgColor: 'bg-red-100', textColor: 'text-red-700', borderColor: 'border-red-200' },
    'PETITION': { label: 'Petition', bgColor: 'bg-purple-100', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
    'MOTION': { label: 'Motion', bgColor: 'bg-indigo-100', textColor: 'text-indigo-700', borderColor: 'border-indigo-200' },
    'OPPOSITION': { label: 'Opposition', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700', borderColor: 'border-yellow-200' },
    'SUPPORT': { label: 'Support', bgColor: 'bg-emerald-100', textColor: 'text-emerald-700', borderColor: 'border-emerald-200' },
    'WITHDRAWAL': { label: 'Withdrawal', bgColor: 'bg-gray-100', textColor: 'text-gray-700', borderColor: 'border-gray-200' },
    'INITIAL': { label: 'Initial', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700', borderColor: 'border-cyan-200' },
    'FINAL': { label: 'Final', bgColor: 'bg-pink-100', textColor: 'text-pink-700', borderColor: 'border-pink-200' },
    'CORRESPONDENCE': { label: 'Correspondence', bgColor: 'bg-slate-100', textColor: 'text-slate-700', borderColor: 'border-slate-200' },
    'EXPRESS-COMMENT': { label: 'Express Comment', bgColor: 'bg-purple-100', textColor: 'text-purple-700', borderColor: 'border-purple-200' },
  };

  const typeInfo = typeMap[filingType.toUpperCase()] || {
    label: filingType,
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
    borderColor: 'border-gray-200'
  };

  return typeInfo;
};

export const getAvailableFilingTypes = (filings: any[]): string[] => {
  const typeSet = new Set<string>();
  
  filings.forEach((filing: any) => {
    if (filing.submissiontype?.short) {
      typeSet.add(filing.submissiontype.short);
    }
  });
  
  // Always include EXPRESS-COMMENT as an available filing type
  typeSet.add('EXPRESS-COMMENT');
  
  return Array.from(typeSet).sort();
};

export const getFilerDocumentCount = (
  filerName: string, 
  filings: any[], 
  filingDetails: { [key: string]: any }
): number => {
  let totalDocuments = 0;
  
  filings.forEach((filing: any) => {
    if (filing.filers && filing.filers[0] && filing.filers[0].name === filerName) {
      // Check if we have filing details with documents
      const details = filingDetails[filing.id_submission];
      if (details && details.documents && details.documents.length > 0) {
        totalDocuments += details.documents.length;
      } else {
        // If no details loaded yet, assume at least 1 document per filing
        totalDocuments += 1;
      }
    }
  });
  
  console.log(`🔢 Filer "${filerName}" has ${totalDocuments} documents across ${filings.filter((f: any) => f.filers && f.filers[0] && f.filers[0].name === filerName).length} filings`);
  return totalDocuments;
};
